<template>
  <div
    class="popup"
    :style="{ top: `${y}%`, left: `${x}%` }"
  >
    <div class="popup-header">
      <h3>Add Comment</h3>
      <button
        class="close-btn"
        @click="$emit('close')"
      >
        ×
      </button>
    </div>

    <div class="popup-content">
      <textarea
        v-model="content"
        placeholder="Write comment..."
        class="comment-textarea"
        @input="handleMentionInput"
      />

      <div class="mentions-container">
        <div class="selected-mentions">
          <span
            v-for="mention in selectedMentions"
            :key="mention.id"
            class="mention-tag"
          >
            {{ mention.full_name ? mention.full_name : mention.email }}
            <button @click="removeMention(mention)">×</button>
          </span>
        </div>

        <div
          v-if="showMentionSuggestions"
          class="mention-suggestions"
        >
          <div
            v-for="user in filteredUsers"
            :key="user.id"
            class="mention-suggestion"
            @click="addMention(user)"
          >
            {{ user.full_name ? user.full_name : user.email }}
          </div>
        </div>
      </div>
    </div>

    <div class="popup-footer">
      <button
        class="submit-btn"
        @click="submit"
      >
        Submit
      </button>
      <button
        class="cancel-btn"
        @click="$emit('close')"
      >
        Cancel
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

const props = defineProps(['x', 'y', 'submitters', 'parentId'])
const emit = defineEmits(['submit', 'close'])

const content = ref('')
const selectedMentions = ref([])
const showMentionSuggestions = ref(false)
const mentionSearch = ref('')

onMounted(() => {
  console.log('[CommentPopup] Component has been mounted. Props:', props)
})

const filteredUsers = computed(() => {
  if (!mentionSearch.value) return []
  const search = mentionSearch.value.toLowerCase()
  const listSubmitters = JSON.parse(props.submitters)
  return listSubmitters.filter(user => {
    const isBcc = user.preferences && (user.preferences.is_bcc_contact === 'true' || user.preferences.is_real_bcc_contact === 'true')
    return !isBcc &&
      (user.email.toLowerCase().includes(search) || user.full_name.toLowerCase().includes(search)) &&
      !selectedMentions.value.find(m => m.id === user.id)
  })
})

const handleMentionInput = (event) => {
  const text = event.target.value
  const lastWord = text.split(' ').pop()

  if (lastWord.startsWith('@')) {
    mentionSearch.value = lastWord.slice(1)
    showMentionSuggestions.value = true
  } else {
    showMentionSuggestions.value = false
  }
}

const addMention = (user) => {
  const textarea = document.querySelector('.comment-textarea')

  if (!textarea) return

  const cursorPos = textarea.selectionStart
  const value = textarea.value

  // Find the word just before the cursor (starting with @)
  const beforeCursor = value.slice(0, cursorPos)
  const afterCursor = value.slice(cursorPos)

  const words = beforeCursor.split(/\s+/)
  const lastWord = words.pop()

  if (lastWord && lastWord.startsWith('@')) {
    const newBefore = words.join(' ')
    const newText = `${newBefore} ${user.full_name} ${afterCursor}`.trim()

    textarea.value = newText
    content.value = newText

    // Move cursor to after the inserted name
    const newCursorPos = (newBefore + ' ' + user.full_name + ' ').length
    textarea.setSelectionRange(newCursorPos, newCursorPos)
  }

  selectedMentions.value.push(user)
  showMentionSuggestions.value = false
  mentionSearch.value = ''
}

const removeMention = (mention) => {
  selectedMentions.value = selectedMentions.value.filter(m => m.id !== mention.id)
}

const submit = () => {
  emit('submit', {
    x: props.x,
    y: props.y,
    page_number: 1,
    content: content.value,
    mentioned_user_ids: selectedMentions.value.map(m => m.id),
    parent_id: props.parentId
  })
}
</script>

<style scoped>
.popup {
  position: absolute;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  width: 300px;
  z-index: 21;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.popup-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}

.popup-content {
  padding: 16px;
}

.comment-textarea {
  width: 100%;
  min-height: 80px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 12px;
  resize: vertical;
}

.mentions-container {
  position: relative;
  margin-bottom: 12px;
}

.selected-mentions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
}

.mention-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.mention-tag button {
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  padding: 0 2px;
}

.mention-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 21;
}

.mention-suggestion {
  padding: 8px 12px;
  cursor: pointer;
}

.mention-suggestion:hover {
  background: #f5f5f5;
}

.visibility-control {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.visibility-select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.popup-footer {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.submit-btn, .cancel-btn {
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.submit-btn {
  background: #1976d2;
  color: white;
  border: none;
}

.cancel-btn {
  background: white;
  border: 1px solid #ddd;
}
</style>
